import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { itineraryApi } from '../services/api';
import { downloadTimelinePdf, getTimelinePdfFileName } from '../utils/downloadTimelinePdf';
import { ShareStoryDialog } from '../components/timeline/ShareStoryDialog';
import {
  Loader2, ArrowLeft, MapPin, Calendar, Camera, DollarSign, Star,
  Share2, Check, Plane, Bus, Train, Car, X,
  Edit3, Save, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (d) => {
  if (!d) return '';
  const date = new Date(d + 'T00:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const TransportIcon = ({ type }) => {
  const t = (type || '').toLowerCase();
  if (t.includes('flight') || t.includes('plane')) return <Plane className="w-3 h-3" />;
  if (t.includes('bus')) return <Bus className="w-3 h-3" />;
  if (t.includes('train')) return <Train className="w-3 h-3" />;
  return <Car className="w-3 h-3" />;
};

const TripTimelinePage = () => {
  const { id: itineraryId } = useParams();
  const navigate = useNavigate();
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState('');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editNotes, setEditNotes] = useState({});
  const [editActivities, setEditActivities] = useState({});
  const [saving, setSaving] = useState(false);
  const [savingDay, setSavingDay] = useState(null);
  const [savedDays, setSavedDays] = useState(new Set());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const loadTimeline = useCallback(async () => {
    setLoading(true);
    try {
      const res = await itineraryApi.getTimeline(itineraryId);
      setTimeline(res.data);
    } catch {
      toast.error('Failed to load timeline');
    } finally {
      setLoading(false);
    }
    // itineraryApi, setLoading, setTimeline, toast are stable refs
  }, [itineraryId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadTimeline();
  }, [loadTimeline]);

  const handleSharePdf = async () => {
    setSharing(true);
    try {
      const res = await itineraryApi.createTimelineShare(itineraryId);
      if (res.data.success) {
        const url = `${window.location.origin}/shared/timeline/${res.data.share_id}`;
        setShareUrl(url);
        setShowShareDialog(true);
      }
    } catch {
      toast.error('Failed to create share link');
    } finally {
      setSharing(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!timeline) return;
    downloadTimelinePdf(timeline);
    toast.success('Generating PDF...');
  };

  const handleStartEdit = () => {
    const notes = {};
    const acts = {};
    timeline.days?.forEach(d => {
      notes[String(d.day_number)] = d.story_note || '';
      acts[String(d.day_number)] = d.activities.map(a => ({
        name: a.name || '',
        description: a.description || '',
        time: a.time || '',
      }));
    });
    setEditNotes(notes);
    setEditActivities(acts);
    setEditing(true);
    setSavedDays(new Set());
    setHasUnsavedChanges(false);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditNotes({});
    setEditActivities({});
    setSavedDays(new Set());
    setHasUnsavedChanges(false);
  };

  const updateNote = (dayNum, value) => {
    setEditNotes(prev => ({ ...prev, [String(dayNum)]: value }));
    setHasUnsavedChanges(true);
  };

  const updateActivity = (dayNum, actIdx, field, value) => {
    setEditActivities(prev => {
      const dayActs = [...(prev[String(dayNum)] || [])];
      dayActs[actIdx] = { ...dayActs[actIdx], [field]: value };
      return { ...prev, [String(dayNum)]: dayActs };
    });
    setHasUnsavedChanges(true);
  };

  // Save a single day's changes
  const handleSaveDay = async (dayNum) => {
    setSavingDay(dayNum);
    try {
      const allNotes = { ...editNotes };
      const allEdits = { ...editActivities };
      await itineraryApi.saveTimelineNotes(itineraryId, allNotes, allEdits);
      toast.success(`Day ${dayNum} saved!`);
      // Mark this day as saved — it exits edit mode for this card
      setSavedDays(prev => new Set([...prev, dayNum]));
      // Refresh timeline data so view mode shows updated content
      const res = await itineraryApi.getTimeline(itineraryId);
      setTimeline(res.data);
      // Keep edit state for unsaved days
      const newNotes = {};
      const newActs = {};
      res.data.days?.forEach(d => {
        newNotes[String(d.day_number)] = d.story_note || '';
        newActs[String(d.day_number)] = d.activities.map(a => ({
          name: a.name || '', description: a.description || '', time: a.time || '',
        }));
      });
      setEditNotes(newNotes);
      setEditActivities(newActs);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSavingDay(null);
    }
  };

  // Save all changes at once
  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await itineraryApi.saveTimelineNotes(itineraryId, editNotes, editActivities);
      toast.success('All changes saved!');
      setEditing(false);
      setSavedDays(new Set());
      setHasUnsavedChanges(false);
      loadTimeline();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Social share helpers
  const pdfFileName = timeline ? getTimelinePdfFileName(timeline.trip_input) : '';
  const shareText = timeline
    ? `Check out my trip to ${timeline.trip_input?.destination_city || 'an amazing destination'}! Planned with Triple A Travel Solutions. Book yours at tripleabooking.com`
    : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70">
        <Header />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gold" />
        </div>
      </div>
    );
  }

  if (!timeline) return null;

  const { trip_input, days, summary, review, budget } = timeline;
  const city = trip_input?.destination_cities?.length > 1
    ? trip_input.destination_cities.join(' > ')
    : trip_input?.destination_city || 'Unknown';
  const country = trip_input?.destination_country || '';

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="trip-timeline-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-[#0d4a6b] to-sky/80" />
        <div className="absolute top-20 right-10 w-96 h-96 bg-gold/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-20 w-80 h-80 bg-sky/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <Header />

      <main className="pt-24 pb-16 px-4 sm:px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Back + Actions */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <Link to={`/itinerary/${itineraryId}`} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-itinerary">
              <ArrowLeft className="w-4 h-4" /> Back to Itinerary
            </Link>
            <div className="flex flex-wrap gap-2">
              {editing ? (
                <>
                  <Button
                    onClick={handleSaveAll}
                    disabled={saving}
                    className="bg-forest hover:bg-forest/90 text-white rounded-full text-sm font-semibold shadow-lg"
                    data-testid="save-all-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save All
                  </Button>
                  <Button
                    onClick={handleCancelEdit}
                    variant="outline"
                    className="text-white border-white/30 hover:bg-white/10 rounded-full text-sm"
                    data-testid="cancel-edit-btn"
                  >
                    <X className="w-4 h-4 mr-2" /> Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleStartEdit}
                    className="bg-white/20 hover:bg-white/30 text-white rounded-full text-sm font-semibold shadow-lg"
                    data-testid="edit-story-btn"
                  >
                    <Edit3 className="w-4 h-4 mr-2" /> Edit Story
                  </Button>
                  <Button
                    onClick={handleSharePdf}
                    disabled={sharing}
                    className="bg-gold hover:bg-gold/90 text-ocean rounded-full text-sm font-semibold shadow-lg"
                    data-testid="share-pdf-btn"
                  >
                    {sharing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
                    Share Story
                  </Button>
                  <Link to={`/itinerary/${itineraryId}/blog`}>
                    <Button
                      className="bg-ocean-light hover:bg-ocean text-white rounded-full text-sm font-semibold shadow-lg"
                      data-testid="convert-to-blog-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" /> Convert to Blog
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Hero Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <p className="text-gold text-sm tracking-[0.2em] uppercase font-semibold mb-2">Trip Story</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-white drop-shadow-lg mb-2">{city}</h1>
            <p className="text-white/60 text-lg">{country}</p>
            {trip_input?.start_date && (
              <p className="text-white/50 mt-2 text-sm">{formatDate(trip_input.start_date)} - {formatDate(trip_input.end_date)}</p>
            )}
          </motion.div>

          {/* Summary Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-12"
          >
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 text-center">
              <Calendar className="w-5 h-5 text-gold mx-auto mb-1" />
              <p className="text-2xl font-bold text-white">{summary?.total_days || 0}</p>
              <p className="text-white/50 text-xs">Days</p>
            </div>
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 text-center">
              <MapPin className="w-5 h-5 text-gold mx-auto mb-1" />
              <p className="text-2xl font-bold text-white">{summary?.cities_visited?.length || 0}</p>
              <p className="text-white/50 text-xs">Cities</p>
            </div>
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 text-center">
              <Camera className="w-5 h-5 text-gold mx-auto mb-1" />
              <p className="text-2xl font-bold text-white">{summary?.total_photos || 0}</p>
              <p className="text-white/50 text-xs">Photos</p>
            </div>
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 text-center">
              <DollarSign className="w-5 h-5 text-gold mx-auto mb-1" />
              <p className="text-2xl font-bold text-white">${summary?.total_spent?.toFixed(0) || '0'}</p>
              <p className="text-white/50 text-xs">Spent</p>
            </div>
          </motion.div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 sm:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-gold via-sky/50 to-transparent" />

            {days.map((day, idx) => (
              <motion.div
                key={day.day_number}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * idx }}
                className="relative pl-16 sm:pl-20 mb-10"
                data-testid={`timeline-day-${day.day_number}`}
              >
                {/* Day marker */}
                <div className="absolute left-3 sm:left-5 top-0 w-7 h-7 rounded-full bg-gold border-4 border-ocean flex items-center justify-center z-10">
                  <span className="text-ocean text-[10px] font-bold">{day.day_number}</span>
                </div>

                {/* Day card */}
                <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-white/30 overflow-hidden">
                  {/* Day header */}
                  <div className="bg-gradient-to-r from-ocean to-ocean-light px-5 py-3 flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-semibold text-sm">{day.title || `Day ${day.day_number}`}</h3>
                      <div className="flex items-center gap-3 mt-0.5">
                        {day.date && <span className="text-white/60 text-xs">{formatDate(day.date)}</span>}
                        {day.city && (
                          <span className="text-gold text-xs flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {day.city}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {day.day_expense_total > 0 && (
                        <span className="bg-gold/20 text-gold px-3 py-1 rounded-full text-xs font-bold">
                          ${day.day_expense_total.toFixed(0)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="p-5">
                    {/* Determine if this specific day is in edit mode */}
                    {(() => {
                      const isDayEditing = editing && !savedDays.has(day.day_number);
                      return (
                        <>
                    {/* Story Note (view or edit) */}
                    {isDayEditing ? (
                      <div className="mb-4">
                        <label className="text-xs text-charcoal/50 font-medium mb-1 block">Your Story Note</label>
                        <textarea
                          value={editNotes[String(day.day_number)] || ''}
                          onChange={(e) => updateNote(day.day_number, e.target.value)}
                          placeholder="Write your story for this day... What did you experience? How did it feel?"
                          className="w-full bg-gold/5 border border-gold/20 rounded-xl p-3 text-charcoal text-sm resize-none focus:outline-none focus:ring-2 focus:ring-gold/40 min-h-[80px]"
                          rows={3}
                          data-testid={`edit-note-${day.day_number}`}
                        />
                      </div>
                    ) : day.story_note ? (
                      <div className="mb-4 bg-gold/5 border-l-4 border-gold/40 rounded-r-xl px-4 py-3">
                        <p className="text-charcoal/80 text-sm italic leading-relaxed break-words">{day.story_note}</p>
                      </div>
                    ) : null}

                    {/* Photos */}
                    {day.photos.length > 0 && (
                      <div className="mb-4">
                        <div className={`grid gap-2 ${day.photos.length === 1 ? 'grid-cols-1' : day.photos.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                          {day.photos.slice(0, 3).map((photo) => (
                            <div key={photo.id} className="relative rounded-xl overflow-hidden h-32 sm:h-40">
                              <img
                                src={`${API_URL}/api/journal/file/${encodeURIComponent(photo.storage_path)}`}
                                alt={photo.caption || 'Travel photo'}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                              {photo.caption && (
                                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                  <p className="text-white text-xs truncate">{photo.caption}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        {day.photos.length > 3 && (
                          <p className="text-charcoal/40 text-xs mt-1 text-right">+{day.photos.length - 3} more photos</p>
                        )}
                      </div>
                    )}

                    {/* Activities — editable or read-only */}
                    {isDayEditing ? (
                      <div className="space-y-3 mb-3">
                        <label className="text-xs text-charcoal/50 font-medium block">Activities</label>
                        {(editActivities[String(day.day_number)] || []).map((act, i) => (
                          <div key={`edit-${day.day_number}-${i}-${act.time || ''}`} className="bg-ocean/5 rounded-xl p-3 border border-ocean/10">
                            <div className="flex items-center gap-2 mb-2">
                              {act.time && (
                                <span className="text-ocean/60 text-xs font-mono bg-ocean/10 px-2 py-0.5 rounded">{act.time}</span>
                              )}
                            </div>
                            <input
                              type="text"
                              value={act.name}
                              onChange={(e) => updateActivity(day.day_number, i, 'name', e.target.value)}
                              className="w-full bg-white border border-ocean/15 rounded-lg px-3 py-2 text-charcoal text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ocean/30 mb-2"
                              placeholder="Activity name"
                              data-testid={`edit-activity-name-${day.day_number}-${i}`}
                            />
                            <input
                              type="text"
                              value={act.description}
                              onChange={(e) => updateActivity(day.day_number, i, 'description', e.target.value)}
                              className="w-full bg-white border border-ocean/15 rounded-lg px-3 py-2 text-charcoal/70 text-xs focus:outline-none focus:ring-2 focus:ring-ocean/30"
                              placeholder="Description / location (optional)"
                              data-testid={`edit-activity-desc-${day.day_number}-${i}`}
                            />
                          </div>
                        ))}
                      </div>
                    ) : day.activities.length > 0 ? (
                      <div className="space-y-2 mb-3">
                        {day.activities.map((act, i) => (
                          <div key={`act-${day.day_number}-${act.name}-${act.time || i}`} className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start gap-2">
                                {act.time && <span className="text-ocean/50 text-xs font-mono flex-shrink-0 mt-0.5">{act.time}</span>}
                                <p className="text-charcoal font-medium text-sm break-words">{act.name}</p>
                              </div>
                              {act.description && <p className="text-charcoal/50 text-xs mt-0.5 break-words">{act.description}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {/* Transport */}
                    {day.transport && day.transport.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-charcoal/10">
                        {day.transport.map((t, i) => (
                          <span key={`trans-${day.day_number}-${t.type}-${i}`} className="flex items-center gap-1.5 bg-sky/10 text-sky px-2.5 py-1 rounded-full text-xs">
                            <TransportIcon type={t.type} />
                            {t.type || t.description}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Expenses */}
                    {day.expenses && day.expenses.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-charcoal/10">
                        <div className="flex flex-wrap gap-2">
                          {day.expenses.map((exp, i) => (
                            <span key={`exp-${day.day_number}-${exp.category}-${exp.amount}-${i}`} className="bg-gold/10 text-gold-dark px-2.5 py-1 rounded-full text-xs">
                              {exp.category}: {exp.currency} {exp.amount.toFixed(0)} - {exp.description}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Per-card Save button (only in edit mode for this day) */}
                    {isDayEditing && (
                      <div className="mt-4 pt-3 border-t border-charcoal/10 flex justify-end">
                        <Button
                          onClick={() => handleSaveDay(day.day_number)}
                          disabled={savingDay === day.day_number}
                          size="sm"
                          className="bg-ocean hover:bg-ocean-light text-white rounded-full text-xs px-4 h-8 shadow"
                          data-testid={`save-day-${day.day_number}`}
                        >
                          {savingDay === day.day_number ? (
                            <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                          ) : (
                            <Save className="w-3 h-3 mr-1.5" />
                          )}
                          Save Day {day.day_number}
                        </Button>
                      </div>
                    )}

                    {/* Saved indicator — show when this day was just saved */}
                    {editing && savedDays.has(day.day_number) && (
                      <div className="mt-4 pt-3 border-t border-charcoal/10 flex items-center justify-between">
                        <span className="text-forest text-xs font-medium flex items-center gap-1.5">
                          <Check className="w-3.5 h-3.5" /> Saved
                        </span>
                        <Button
                          onClick={() => setSavedDays(prev => {
                            const next = new Set(prev);
                            next.delete(day.day_number);
                            return next;
                          })}
                          variant="ghost"
                          size="sm"
                          className="text-ocean/60 hover:text-ocean text-xs h-7 px-3"
                          data-testid={`re-edit-day-${day.day_number}`}
                        >
                          <Edit3 className="w-3 h-3 mr-1" /> Edit Again
                        </Button>
                      </div>
                    )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Review at the end */}
            {review && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * days.length }}
                className="relative pl-16 sm:pl-20 mb-10"
                data-testid="timeline-review"
              >
                <div className="absolute left-3 sm:left-5 top-0 w-7 h-7 rounded-full bg-gold border-4 border-ocean flex items-center justify-center z-10">
                  <Star className="w-3 h-3 text-ocean" />
                </div>
                <div className="bg-gold/20 backdrop-blur-xl rounded-2xl p-5 border border-gold/30">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={`star-${i}`} className={`w-4 h-4 ${i < review.rating ? 'text-gold fill-gold' : 'text-white/20'}`} />
                    ))}
                  </div>
                  <p className="text-white/80 text-sm italic">"{review.text}"</p>
                </div>
              </motion.div>
            )}

            {/* End marker */}
            <div className="relative pl-16 sm:pl-20">
              <div className="absolute left-3 sm:left-5 top-0 w-7 h-7 rounded-full bg-white/20 border-4 border-ocean flex items-center justify-center z-10">
                <Check className="w-3 h-3 text-white" />
              </div>
              <p className="text-white/40 text-sm pt-1">End of trip</p>
            </div>
          </div>

          {/* Bottom actions */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
            <Button
              onClick={() => navigate(`/itinerary/${itineraryId}`)}
              variant="outline"
              className="text-white border-white/30 hover:bg-white/10 rounded-full px-6"
              data-testid="back-to-itinerary-bottom"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Itinerary
            </Button>
            {editing && (
              <Button
                onClick={handleSaveAll}
                disabled={saving}
                className="bg-gold hover:bg-gold/90 text-ocean rounded-full px-6 font-semibold shadow-lg"
                data-testid="save-all-bottom-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save All Changes
              </Button>
            )}
          </div>
        </div>
      </main>

      <ShareStoryDialog
        show={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        shareUrl={shareUrl}
        pdfFileName={pdfFileName}
        shareText={shareText}
        onDownloadPdf={handleDownloadPdf}
      />
    </div>
  );
};

export default TripTimelinePage;
